import React, { useEffect, useRef } from 'react'
import { Group, Rect, Text } from 'react-konva'
import type { HighlightArea } from '../types'
import {
  BOUNDARY_GAP, CELL_GAP, CELL_SIZE, MAP_OFFSET_X, MAP_OFFSET_Y, SIDE_SIZE,
  BANK_OFFSET_X, BANK_OFFSET_Y,
  MODE_SELECTOR_X, MODE_SELECTOR_Y,
  PROBLEM_OFFSET_X, PROBLEM_OFFSET_Y, CARD_WIDTH, PROBLEM_CARD_HEIGHT,
  TELEPORT_BTN_SIZE, TELEPORT_BTN_TOP_Y, TELEPORT_TOP_5_X, TELEPORT_TOP_10_X,
  TELEPORT_BTN_LEFT_X, TELEPORT_LEFT_5_Y, TELEPORT_LEFT_10_Y,
  FONTS,
} from '../constants'

interface TutorialHighlightProps {
  area: HighlightArea | undefined
  stepPassed: boolean
}

/** HighlightArea -> pixel rect */
function areaToRect(area: HighlightArea): { x: number; y: number; width: number; height: number } | null {
  switch (area.type) {
    case 'map': {
      const sc = area.startCol ?? 0
      const sr = area.startRow ?? 0
      const ec = area.endCol ?? sc
      const er = area.endRow ?? sr
      const bxS = sc >= 5 ? BOUNDARY_GAP : 0
      const byS = sr >= 5 ? BOUNDARY_GAP : 0
      const bxE = ec >= 5 ? BOUNDARY_GAP : 0
      const byE = er >= 5 ? BOUNDARY_GAP : 0
      const x = MAP_OFFSET_X + sc * (CELL_SIZE + CELL_GAP) + bxS
      const y = MAP_OFFSET_Y + sr * (CELL_SIZE + CELL_GAP) + byS
      const x2 = MAP_OFFSET_X + ec * (CELL_SIZE + CELL_GAP) + bxE + CELL_SIZE
      const y2 = MAP_OFFSET_Y + er * (CELL_SIZE + CELL_GAP) + byE + CELL_SIZE
      return { x: x - 4, y: y - 4, width: x2 - x + 8, height: y2 - y + 8 }
    }
    case 'bank': {
      const sc = area.startCol ?? 0
      const sr = area.startRow ?? 0
      const ec = area.endCol ?? sc
      const er = area.endRow ?? sr
      const x = BANK_OFFSET_X + 100 + sc * (CELL_SIZE + CELL_GAP)
      const y = BANK_OFFSET_Y + 25 + sr * (CELL_SIZE + CELL_GAP)
      const x2 = BANK_OFFSET_X + 100 + ec * (CELL_SIZE + CELL_GAP) + CELL_SIZE
      const y2 = BANK_OFFSET_Y + 25 + er * (CELL_SIZE + CELL_GAP) + CELL_SIZE
      return { x: x - 4, y: y - 4, width: x2 - x + 8, height: y2 - y + 8 }
    }
    case 'side': {
      const idx = area.sideIndex ?? 0
      const boundary = idx >= 5 ? BOUNDARY_GAP : 0
      if (area.sideDir === 'x') {
        const sx = MAP_OFFSET_X + idx * (CELL_SIZE + CELL_GAP) + boundary + (CELL_SIZE - SIDE_SIZE) / 2
        const sy = MAP_OFFSET_Y - SIDE_SIZE - 6
        return { x: sx - 4, y: sy - 4, width: SIDE_SIZE + 8, height: SIDE_SIZE + 8 }
      } else {
        const sx = MAP_OFFSET_X - SIDE_SIZE - 6
        const sy = MAP_OFFSET_Y + idx * (CELL_SIZE + CELL_GAP) + boundary + (CELL_SIZE - SIDE_SIZE) / 2
        return { x: sx - 4, y: sy - 4, width: SIDE_SIZE + 8, height: SIDE_SIZE + 8 }
      }
    }
    case 'mode-selector':
      return { x: MODE_SELECTOR_X - 4, y: MODE_SELECTOR_Y - 4, width: 349, height: 180 }
    case 'toolbar':
      return { x: PROBLEM_OFFSET_X - 4, y: PROBLEM_OFFSET_Y + PROBLEM_CARD_HEIGHT / 2 - 4, width: CARD_WIDTH + 8, height: PROBLEM_CARD_HEIGHT / 2 + 8 }
    case 'teleport-btn': {
      const pad = 6
      const s = TELEPORT_BTN_SIZE + pad * 2
      switch (area.buttonId) {
        case 'row5': return { x: TELEPORT_TOP_5_X - pad, y: TELEPORT_BTN_TOP_Y - pad, width: s, height: s }
        case 'row10': return { x: TELEPORT_TOP_10_X - pad, y: TELEPORT_BTN_TOP_Y - pad, width: s, height: s }
        case 'col5': return { x: TELEPORT_BTN_LEFT_X - pad, y: TELEPORT_LEFT_5_Y - pad, width: s, height: s }
        case 'col10': return { x: TELEPORT_BTN_LEFT_X - pad, y: TELEPORT_LEFT_10_Y - pad, width: s, height: s }
        default: return null
      }
    }
    default:
      return null
  }
}

/** Konva highlight rect (pulsing dashed border) */
export const TutorialHighlight: React.FC<TutorialHighlightProps> = ({ area, stepPassed }) => {
  const rectRef = useRef<any>(null)

  // Pulsing animation
  useEffect(() => {
    const node = rectRef.current
    if (!node || !area || stepPassed) return

    let frame: number
    let t = 0
    const animate = () => {
      t += 0.05
      node.dashOffset(Math.sin(t) * 8)
      node.opacity(0.6 + Math.sin(t * 2) * 0.3)
      node.getLayer()?.batchDraw()
      frame = requestAnimationFrame(animate)
    }
    frame = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frame)
  }, [area, stepPassed])

  if (!area) return null
  const rect = areaToRect(area)
  if (!rect) return null

  return (
    <Group listening={false}>
      <Rect
        ref={rectRef}
        x={rect.x}
        y={rect.y}
        width={rect.width}
        height={rect.height}
        stroke={stepPassed ? '#4CAF50' : '#E91E63'}
        strokeWidth={stepPassed ? 3 : 2.5}
        dash={stepPassed ? undefined : [8, 4]}
        cornerRadius={6}
        fill="transparent"
      />
    </Group>
  )
}

/** Konva tutorial instruction panel (은행 하단, Stage 내부 렌더링) */
export const TutorialPanel: React.FC<{
  x: number
  y: number
  width: number
  instruction: string
  hint?: string
  showHint: boolean
  stepPassed: boolean
  moduleName: string
  progress: string
  completed?: boolean
  onStop: () => void
}> = ({ x, y, width, instruction, hint, showHint, stepPassed, moduleName, progress, completed, onStop }) => {
  const pad = 12
  const contentW = width - pad * 2
  const headerFontSize = 16
  const instrFontSize = 18
  const hintFontSize = 16
  const lineH = 1.8

  // Layout positions (top-down)
  let cy = y + pad
  const headerY = cy
  cy += Math.ceil(headerFontSize * lineH) + 6

  const instrText = `${completed ? '\u2714 ' : ''}${instruction}`
  const instrY = cy
  cy += Math.ceil(instrFontSize * lineH) * 2 + 4 // ~2줄 분량

  const showHintLine = !!(showHint && hint && !stepPassed)
  const hintY = cy
  if (showHintLine) {
    cy += Math.ceil(hintFontSize * lineH) + 4
  }

  const totalHeight = cy - y + pad

  // Exit button
  const exitBtnW = 40
  const exitBtnH = 18
  const exitBtnX = x + width - pad - exitBtnW
  const exitBtnY = headerY

  return (
    <Group>
      {/* Background */}
      <Rect
        x={x} y={y}
        width={width} height={totalHeight}
        fill={completed ? '#1B3A2A' : '#1E1E5A'}
        stroke={completed ? '#4CAF50' : '#3F3FA0'}
        strokeWidth={2}
        cornerRadius={10}
      />

      {/* Header */}
      <Text
        x={x + pad} y={headerY}
        width={contentW - (completed ? exitBtnW + 4 : 0)}
        text={`${moduleName} (${progress})`}
        fontSize={headerFontSize}
        fontStyle="bold"
        fontFamily={FONTS.bold}
        fill="#AAAACC"
        listening={false}
      />

      {/* Exit button (정답 완료 후에만) */}
      {completed && (
        <Group onClick={onStop} onTap={onStop}>
          <Rect
            x={exitBtnX} y={exitBtnY}
            width={exitBtnW} height={exitBtnH}
            fill="#3a3a7a"
            stroke="#5a5aaa"
            strokeWidth={1}
            cornerRadius={4}
          />
          <Text
            x={exitBtnX} y={exitBtnY}
            width={exitBtnW} height={exitBtnH}
            text="종료"
            fontSize={11}
            fontFamily={FONTS.bold}
            fill="#CCCCEE"
            align="center"
            verticalAlign="middle"
            listening={false}
          />
        </Group>
      )}

      {/* Instruction */}
      <Text
        x={x + pad} y={instrY}
        width={contentW}
        text={instrText}
        fontSize={instrFontSize}
        fontStyle="bold"
        fontFamily={FONTS.bold}
        fill={completed ? '#66BB6A' : '#FFFFFF'}
        wrap="word"
        listening={false}
      />

      {/* Hint */}
      {showHintLine && (
        <Text
          x={x + pad} y={hintY}
          width={contentW}
          text={hint!}
          fontSize={hintFontSize}
          fontFamily={FONTS.regular}
          fill="#64B5F6"
          fontStyle="italic"
          wrap="word"
          listening={false}
        />
      )}
    </Group>
  )
}
